// pattern: Functional Core

import JSON5 from 'json5'

export const SUPPORTED_METHODS = [
    'get',
    'post',
    'put',
    'patch',
    'delete'
] as const

export type HttpMethod = typeof SUPPORTED_METHODS[number]

export const CURATED_STATUS_CODES = [
    '200', '201', '204', '400', '401', '404', '500'
] as const

export const CURATED_MEDIA_TYPES = [
    'application/json',
    'application/xml',
    'text/plain',
    'text/html',
    'application/octet-stream',
    'multipart/form-data',
    'application/x-www-form-urlencoded'
] as const

export type ParameterLocation = 'query' | 'path'
export type ParameterType = 'string' | 'number' | 'integer' | 'boolean'

export interface ParameterEntry {
    name:string;
    in:ParameterLocation;
    type:ParameterType;
    required:boolean;
    description:string;
}

export interface OpenApiParameter {
    name:string;
    in:ParameterLocation;
    required:boolean;
    description:string;
    schema:{ type:ParameterType };
}

function toOpenApiParameter (entry:ParameterEntry):OpenApiParameter {
    return {
        name: entry.name,
        in: entry.in,
        required: entry.required,
        description: entry.description,
        schema: { type: entry.type },
    }
}

function fromOpenApiParameter (param:OpenApiParameter):ParameterEntry {
    return {
        name: param.name,
        in: param.in,
        type: param.schema.type,
        required: param.required,
        description: param.description,
    }
}

export const REQUEST_BODY_MEDIA_TYPES = [
    'application/json',
    'multipart/form-data'
] as const

export interface RequestBodyObject {
    content:Record<string, { schema?:unknown; example?:unknown }>;
}

export interface ResponseObject {
    description:string;
    content?:Record<string, { schema?:unknown; example?:unknown }>;
}

export interface RouteOperation {
    summary:string;
    parameters?:OpenApiParameter[];   // query parameters for THIS method
    requestBody?:RequestBodyObject; // body for THIS method (body-capable only)
    responses?:Record<string, ResponseObject>;  // keyed by status code
}

export interface PathItem extends Partial<Record<HttpMethod, RouteOperation>> {
    parameters?:OpenApiParameter[];   // shared PATH parameters only
}

export interface RouteEntry {
    path:string;
    operations:PathItem;
}

export interface CanonicalApiDocument {
    openapi:string;
    info:{
        title:string;
        version:string;
    };
    paths:Record<string, PathItem>;
}

export type SchemaNodeType =
    'string' | 'number' | 'integer' | 'boolean' | 'object' | 'array'

export type StringFormat =
    'date-time' | 'date' | 'email' | 'uri' | 'uuid' | 'password'

export interface XmlModifier {
    name?:string;
    namespace?:string;
    prefix?:string;
    attribute?:boolean;
    wrapped?:boolean;
}

/* eslint-disable no-use-before-define */
export interface SchemaNode {
    type:SchemaNodeType;
    format?:StringFormat;          // string only
    file?:boolean;                 // binary string field
    contentMediaType?:string;      // when file (default octet-stream)
    properties?:PropertyEntry[];   // when object (ordered)
    items?:SchemaNode;             // when array
    xml?:XmlModifier;              // xml media type only
}

export interface PropertyEntry {
    name:string;
    required:boolean;
    schema:SchemaNode;
}

export interface ResponseEntry {
    status:string;        // "200", or a custom numeric code
    description:string;
    mediaType:string;     // content media type, e.g. 'application/json'
    schema:string;        // raw JSON Schema text; '' = none
    schemaModel?:SchemaNode;  // object/xml path: structured tree
    example:string;       // raw example text; '' = none
    expanded:boolean;     // UI: body editor disclosure (never serialized)
}
/* eslint-enable no-use-before-define */

// A media type is treated as JSON when its base type is application/json or it
// uses the structured `+json` suffix (e.g. application/problem+json).
// Media-type parameters (`; charset=...`) are ignored. Only JSON media types
// have their response body validated and reformatted as JSON; every other
// media type is treated as free-form text stored verbatim as an example.
export function isJsonMediaType (mediaType:string):boolean {
    const base = mediaType.split(';')[0].trim().toLowerCase()
    return base === 'application/json' || base.endsWith('+json')
}

export function isXmlMediaType (mediaType:string):boolean {
    const base = (mediaType || '').split(';')[0].trim().toLowerCase()
    return base === 'application/xml' || base === 'text/xml' ||
        base.endsWith('+xml')
}

export interface ResponsesSummary {
    count:number;          // responses with a non-empty status
    codes:string[];        // up to `limit`, ascending numeric
    overflowCount:number;  // max(0, count - limit)
}

// Summarize a method's draft responses for the disclosure indicator: counts
// rows with a non-empty status, sorts those codes ascending numeric (the same
// comparator serializeDocument uses for response keys), slices to `limit`
// (default 4), and reports how many codes were dropped. Duplicate codes are
// kept, so `count` always equals the number of codes shown before overflow.
// The input array is never mutated. `limit` is expected to be >= 1; the
// default is 4 and no caller passes 0 (a 0 limit would show "(N)" with no
// codes), so no special-casing is needed.
export function summarizeResponseStatuses (
    entries:ResponseEntry[],
    limit:number = 4
):ResponsesSummary {
    const codes = entries
        .map(entry => entry.status.trim())
        .filter(status => status !== '')
        .sort((a, b) => Number(a) - Number(b))
    const count = codes.length
    return {
        count,
        codes: codes.slice(0, limit),
        overflowCount: Math.max(0, count - limit)
    }
}

export type MediaTypeKind = 'json' | 'binary' | 'text' | 'object'

// Classify a media type into one editorial mode. The central switch for
// which editing surface renders and which serialization path runs.
export function mediaTypeKind (mediaType:string):MediaTypeKind {
    if (isJsonMediaType(mediaType)) return 'json'
    const base = (mediaType || '').split(';')[0].trim().toLowerCase()
    if (
        base === 'multipart/form-data' ||
        base === 'application/x-www-form-urlencoded' ||
        isXmlMediaType(mediaType)
    ) {
        return 'object'
    }
    if (
        base === 'application/octet-stream' ||
        base === 'application/pdf' ||
        base.startsWith('image/') ||
        base.startsWith('audio/') ||
        base.startsWith('video/')
    ) {
        return 'binary'
    }
    if (base.startsWith('text/')) return 'text'
    // Unknown types are treated as opaque bytes: a lint-clean
    // { type:string, contentMediaType } needs no user input.
    return 'binary'
}

// An object-kind body has content only when the root has at least one
// named property; any non-object root is always treated as content.
function schemaModelHasContent (model:SchemaNode):boolean {
    if (model.type === 'object') {
        return (model.properties || []).some(p => p.name.trim() !== '')
    }
    return true
}

// HTTP responses that cannot carry a body: 1xx informational, 204 No
// Content, 205 Reset Content, and 304 Not Modified (RFC 9110 sec 15.2
// 1xx, 15.3.5 204, 15.3.6 205, 15.4.5 304). Such responses emit no
// `content`, regardless of the selected media type, so a derived non-JSON
// schema is never attached to a bodiless status.
function isBodilessStatus (status:string):boolean {
    const code = parseInt((status || '').trim(), 10)
    if (Number.isNaN(code)) return false
    return code === 204 || code === 205 || code === 304 ||
        (code >= 100 && code <= 199)
}

function toMediaTypeContent (
    mediaType:string,
    schemaText:string,
    exampleText:string,
    schemaModel?:SchemaNode
):{ ok:true; content?:{ schema?:unknown; example?:unknown } }
    | { ok:false } {
    const kind = mediaTypeKind(mediaType)
    const hasExample = (exampleText || '').trim() !== ''

    // --- schema side, per kind ---
    let schema:unknown
    let hasSchema = false
    if (kind === 'json') {
        if ((schemaText || '').trim() !== '') {
            const parsed = parseRequestBody(schemaText)
            if (!parsed.ok) return { ok: false }
            schema = parsed.value
            hasSchema = true
        }
    } else if (kind === 'binary' || kind === 'text') {
        // Auto-derived from the media type; nothing to parse. Emitted
        // whenever the resolved media type is binary/text, so selecting
        // such a media type is itself the signal that a body of that
        // kind exists (AC3.1-3.3, AC7.2).
        const derived = derivedBodySchema(mediaType)
        if (derived) {
            schema = derived
            hasSchema = true
        }
    } else {
        // object: emit from the structured tree when it has content.
        if (schemaModel && schemaModelHasContent(schemaModel)) {
            schema = schemaNodeToJsonSchema(schemaModel)
            hasSchema = true
        }
    }

    if (!hasSchema && !hasExample) return { ok: true, content: undefined }

    const content:{ schema?:unknown; example?:unknown } = {}
    if (hasSchema) content.schema = schema
    if (hasExample) {
        if (isJsonMediaType(mediaType)) {
            const parsed = parseRequestBody(exampleText)
            if (!parsed.ok) return { ok: false }
            content.example = parsed.value
        } else {
            content.example = exampleText
        }
    }
    return { ok: true, content }
}

function fromMediaTypeContent (
    mediaType:string,
    content:{ schema?:unknown; example?:unknown }
):{ schema:string; example:string; schemaModel?:SchemaNode } {
    const kind = mediaTypeKind(mediaType)
    let example = ''
    if (content.example !== undefined) {
        example = (kind !== 'json' &&
            typeof content.example === 'string') ?
            content.example :
            JSON.stringify(content.example, null, 2)
    }
    if (kind === 'object') {
        const schemaModel = content.schema !== undefined ?
            schemaNodeToDraft(content.schema) :
            undefined
        return { schema: '', example, schemaModel }
    }
    if (kind === 'binary' || kind === 'text') {
        // Derived schema; nothing editable to restore. (AC9.4)
        return { schema: '', example }
    }
    const schema = content.schema !== undefined ?
        JSON.stringify(content.schema, null, 2) :
        ''
    return { schema, example }
}

function toResponseObject (
    entry:ResponseEntry
):{ ok:true; value:ResponseObject } | { ok:false } {
    const obj:ResponseObject = { description: entry.description.trim() }
    // A 204/304/1xx response cannot have a body, so never attach content
    // (this is what keeps an empty text/binary 204 from gaining a derived
    // schema). Other statuses emit content per their media-type kind.
    if (isBodilessStatus(entry.status)) {
        return { ok: true, value: obj }
    }
    const mediaType = (entry.mediaType || '').trim() || 'application/json'
    const built = toMediaTypeContent(
        mediaType, entry.schema || '', entry.example || '',
        entry.schemaModel
    )
    if (!built.ok) return { ok: false }
    if (built.content) obj.content = { [mediaType]: built.content }
    return { ok: true, value: obj }
}

function fromResponseObject (
    status:string,
    obj:ResponseObject
):ResponseEntry {
    const keys = obj.content ? Object.keys(obj.content) : []
    const mediaType = keys[0] ?? 'application/json'
    const media = obj.content?.[mediaType]
    const fields = media ?
        fromMediaTypeContent(mediaType, media) :
        { schema: '', example: '' }
    return {
        status,
        description: obj.description,
        mediaType,
        schema: fields.schema,
        schemaModel: fields.schemaModel,
        example: fields.example,
        expanded: false,
    }
}

export interface MethodDraft {
    summary:string;
    queryParameters:ParameterEntry[];   // all in:'query'
    requestBodyMediaType:string;        // 'application/json' | 'multipart/form-data'
    requestBodySchema:string;           // raw JSON Schema text; '' = none
    requestBodySchemaModel?:SchemaNode; // object/xml path: structured tree
    requestBodyExample:string;          // raw example text; '' = none
    responses:ResponseEntry[];          // ordered
}

export interface RouteDraftInput {
    pathInput:string;
    selectedMethods:string[];
    pathParameters:ParameterEntry[];          // all in:'path', shared
    operations:Record<string, MethodDraft>;   // keyed by HTTP method
}

export function emptyResponseEntry ():ResponseEntry {
    return {
        status: '200',
        description: 'OK',
        mediaType: 'application/json',
        schema: '',
        example: '',
        expanded: false,
    }
}

export function emptyMethodDraft ():MethodDraft {
    return {
        summary: '',
        queryParameters: [],
        requestBodyMediaType: 'application/json',
        requestBodySchema: '',
        requestBodyExample: '',
        responses: [emptyResponseEntry()],
    }
}

// Default structured-schema root: an empty object with no properties.
export function emptySchemaNode ():SchemaNode {
    return { type: 'object', properties: [] }
}

export type SchemaPathStep =
    | { kind:'property'; index:number }
    | { kind:'items' }

export type SchemaPath = SchemaPathStep[]

export function emptyProperty ():PropertyEntry {
    return { name: '', required: false, schema: { type: 'string' } }
}

// Apply `change` to the node at `path`, rebuilding ancestors immutably.
// Out-of-range / missing steps are no-ops (return the node unchanged).
export function setNodeAtPath (
    node:SchemaNode,
    path:SchemaPath,
    change:(n:SchemaNode) => SchemaNode
):SchemaNode {
    if (path.length === 0) return change(node)
    const [step, ...rest] = path
    if (step.kind === 'property') {
        const props = node.properties || []
        if (step.index < 0 || step.index >= props.length) return node
        return {
            ...node,
            properties: props.map((p, i) =>
                i === step.index ?
                    { ...p, schema: setNodeAtPath(p.schema, rest, change) } :
                    p
            ),
        }
    }
    if (!node.items) return node
    return { ...node, items: setNodeAtPath(node.items, rest, change) }
}

// Reset a node to a fresh node of `type`, preserving only node-level xml.
// Switching to object/array seeds an empty properties list / string item.
export function applyNodeTypeChange (
    node:SchemaNode,
    type:SchemaNodeType
):SchemaNode {
    const next:SchemaNode = { type }
    if (node.xml) next.xml = node.xml
    if (type === 'object') next.properties = []
    if (type === 'array') next.items = { type: 'string' }
    return next
}

// Toggle the binary "file" flag. Enabling forces a string field with a
// default contentMediaType; disabling returns a plain string. xml preserved.
export function applyNodeFileToggle (
    node:SchemaNode,
    file:boolean
):SchemaNode {
    if (file) {
        const next:SchemaNode = {
            type: 'string',
            file: true,
            contentMediaType: (node.contentMediaType || '').trim() ||
                'application/octet-stream',
        }
        if (node.xml) next.xml = node.xml
        return next
    }
    const off:SchemaNode = { type: 'string' }
    if (node.xml) off.xml = node.xml
    return off
}

export interface SelectedRouteIdentity {
    originalPath:string;
    originalMethods:HttpMethod[];
}

export type ApplyRouteDraftResult = {
    ok:true;
    document:CanonicalApiDocument;
    path:string;
    methods:HttpMethod[];
} | {
    ok:false;
    error:string;
}

export function createDocument ():CanonicalApiDocument {
    return {
        openapi: '3.1.0',
        info: {
            title: 'Easy Open API',
            version: '0.1.0'
        },
        paths: {}
    }
}

export function serializeDocument (document:CanonicalApiDocument):string {
    const sortedPaths = Object.fromEntries(
        Object.entries(document.paths)
            .sort(([left], [right]) => left.localeCompare(right))
            .map(([path, operations]) => {
                const sortedOperations = Object.fromEntries(
                    Object.entries(operations)
                        .sort(([left], [right]) => left.localeCompare(right))
                        .map(([method, op]) => {
                            if (op && op.responses) {
                                const sortedResponses = Object.fromEntries(
                                    Object.entries(op.responses)
                                        .sort(
                                            ([a], [b]) =>
                                                Number(a) - Number(b)
                                        )
                                )
                                return [
                                    method,
                                    { ...op, responses: sortedResponses }
                                ]
                            }
                            return [method, op]
                        })
                ) as Partial<Record<HttpMethod, RouteOperation>>

                return [path, sortedOperations]
            })
    )

    return JSON.stringify({
        ...document,
        paths: sortedPaths
    }, null, 2)
}

export function applyRouteDraft (
    document:CanonicalApiDocument,
    draft:RouteDraftInput
):ApplyRouteDraftResult {
    const path = normalizePath(draft.pathInput)
    const methods = normalizeMethods(draft.selectedMethods)

    if (!path) {
        return {
            ok: false,
            error: 'Route path is required.'
        }
    }

    if (hasInvalidPathWhitespace(path)) {
        return {
            ok: false,
            error: 'Route path cannot contain spaces.'
        }
    }

    if (methods.length === 0) {
        return {
            ok: false,
            error: 'Select at least one HTTP method.'
        }
    }

    // Shared path parameters (validated/normalized once).
    const pathParams = draft.pathParameters || []
    const pathParamError = validateParameters(pathParams)
    if (pathParamError) {
        return { ok: false, error: pathParamError }
    }
    const normalizedPathParams = normalizeParameters(pathParams)

    // One operation per method.
    const operations:Partial<Record<HttpMethod, RouteOperation>> = {}
    for (const method of methods) {
        const md = draft.operations[method] || emptyMethodDraft()

        const queryError = validateParameters(md.queryParameters || [])
        if (queryError) {
            return { ok: false, error: queryError }
        }
        const normalizedQuery = normalizeParameters(md.queryParameters || [])

        const bodyResult = buildRequestBody(
            md.requestBodyMediaType,
            md.requestBodySchema,
            md.requestBodyExample,
            [method],
            md.requestBodySchemaModel
        )
        if (!bodyResult.ok) {
            return { ok: false, error: bodyResult.error }
        }

        const responsesError = validateResponses(md.responses || [])
        if (responsesError) {
            return { ok: false, error: responsesError }
        }
        const responsesResult = buildResponses(md.responses || [])
        if (!responsesResult.ok) {
            return { ok: false, error: responsesResult.error }
        }

        const op:RouteOperation = { summary: md.summary.trim() }
        if (normalizedQuery.length > 0) {
            op.parameters = normalizedQuery
        }
        if (bodyResult.body) {
            op.requestBody = bodyResult.body
        }
        if (responsesResult.responses) {
            op.responses = responsesResult.responses
        }
        operations[method] = op
    }

    const expansion = expandOptionalPathParam(path)
    if (expansion) {
        return applyOptionalExpansion(
            document, expansion, methods, operations,
            normalizedPathParams, path
        )
    }

    const existingEntry = document.paths[path] || {}
    const duplicateMethod = methods.find(method => existingEntry[method])
    if (duplicateMethod) {
        return {
            ok: false,
            error: `Route ${path.toLowerCase()} already has ` +
                `${duplicateMethod.toUpperCase()}.`
        }
    }

    const nextEntry:PathItem = { ...existingEntry }
    methods.forEach(method => {
        nextEntry[method] = operations[method]
    })
    if (normalizedPathParams.length > 0) {
        nextEntry.parameters = normalizedPathParams
    } else {
        delete nextEntry.parameters
    }

    return {
        ok: true,
        path,
        methods,
        document: {
            ...document,
            paths: { ...document.paths, [path]: nextEntry }
        }
    }
}

/**
 * Presence-only completeness check for the in-progress route draft. Returns
 * true when every required input has a value: a non-whitespace path, at least
 * one HTTP method, all shared path parameters have a name, and every selected
 * method's query parameters each have a name and description. It deliberately
 * does NOT evaluate correctness (path spacing, JSON validity, duplicates);
 * those remain on-submit concerns.
 */
export function isRouteDraftComplete (
    draft:Pick<
        RouteDraftInput,
        'pathInput' | 'selectedMethods' | 'pathParameters' | 'operations'
    >
):boolean {
    if (draft.pathInput.trim() === '') return false
    if (draft.selectedMethods.length === 0) return false

    const pathOk = draft.pathParameters.every(
        param => param.name.trim() !== ''
    )
    if (!pathOk) return false

    return draft.selectedMethods.every(method => {
        const md = draft.operations[method]
        if (!md) return true
        const paramsOk = md.queryParameters.every(param =>
            param.name.trim() !== '' &&
            param.description.trim() !== ''
        )
        const responsesOk = (md.responses || []).every(resp => {
            const schemaEmpty = (resp.schema || '').trim() === ''
            const exampleEmpty = (resp.example || '').trim() === ''
            const modelHasContent = !!resp.schemaModel &&
                mediaTypeKind(resp.mediaType || 'application/json') ===
                'object' &&
                resp.schemaModel.type === 'object' &&
                (resp.schemaModel.properties || [])
                    .some(p => p.name.trim() !== '')
            const hasContent = !schemaEmpty || !exampleEmpty || modelHasContent
            const hasMediaType = (resp.mediaType || '').trim() !== ''
            return resp.status.trim() !== '' &&
                resp.description.trim() !== '' &&
                (!hasContent || hasMediaType)
        })
        return paramsOk && responsesOk
    })
}

export function listRouteEntries (document:CanonicalApiDocument):RouteEntry[] {
    return Object.entries(document.paths)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([path, operations]) => ({
            path,
            operations
        }))
}

export function getRouteDraft (
    document:CanonicalApiDocument,
    path:string
):RouteDraftInput | null {
    const normalizedPath = normalizePath(path)
    const pathItem = document.paths[normalizedPath]
    if (!pathItem) return null

    const selectedMethods = SUPPORTED_METHODS.filter(
        method => pathItem[method]
    )

    const operations:Record<string, MethodDraft> = {}
    for (const method of selectedMethods) {
        const op = pathItem[method]!
        const rb = op.requestBody
        const rbKeys = rb ? Object.keys(rb.content) : []
        const rbMediaType = rbKeys[0] ?? 'application/json'
        const rbContent = rb?.content?.[rbMediaType]
        const rbFields = rbContent ?
            fromMediaTypeContent(rbMediaType, rbContent) :
            { schema: '', example: '' }
        operations[method] = {
            summary: op.summary || '',
            queryParameters: (op.parameters || [])
                .filter(p => p.in === 'query')
                .map(fromOpenApiParameter),
            requestBodyMediaType: rbMediaType,
            requestBodySchema: rbFields.schema,
            requestBodySchemaModel: rbFields.schemaModel,
            requestBodyExample: rbFields.example,
            responses: Object.entries(op.responses || {})
                .map(([status, obj]) => fromResponseObject(status, obj)),
        }
    }

    const pathParameters = (pathItem.parameters || [])
        .filter(p => p.in === 'path')
        .map(fromOpenApiParameter)

    return {
        pathInput: normalizedPath,
        selectedMethods,
        pathParameters,
        operations,
    }
}

// A stable string signature of a route draft's MEANINGFUL content (the data
// that would be reconciled into the document on submit): path, methods, shared
// path parameters, and per-method operations. UI-only state is deliberately
// excluded -- the `ResponseEntry.expanded` disclosure flag never appears here,
// so opening/closing a response is not a "change". Methods are visited in
// canonical `SUPPORTED_METHODS` order and optional structured schema models are
// coalesced to `null`, so neither a method reorder nor an absent-vs-undefined
// key can produce a spurious diff. Used by `routeDraftHasPendingChanges`.
export function routeDraftSignature (
    draft:Pick<
        RouteDraftInput,
        'pathInput' | 'selectedMethods' | 'pathParameters' | 'operations'
    >
):string {
    const methods = SUPPORTED_METHODS.filter(
        method => draft.selectedMethods.includes(method)
    )
    const operations = methods.map(method => {
        const op = draft.operations[method]
        if (!op) return [method, null]
        return [method, {
            summary: op.summary,
            queryParameters: op.queryParameters,
            requestBodyMediaType: op.requestBodyMediaType,
            requestBodySchema: op.requestBodySchema,
            requestBodySchemaModel: op.requestBodySchemaModel ?? null,
            requestBodyExample: op.requestBodyExample,
            responses: op.responses.map(resp => ({
                status: resp.status,
                description: resp.description,
                mediaType: resp.mediaType,
                schema: resp.schema,
                schemaModel: resp.schemaModel ?? null,
                example: resp.example,
            })),
        }]
    })
    return JSON.stringify({
        pathInput: draft.pathInput,
        selectedMethods: methods,
        pathParameters: draft.pathParameters,
        operations,
    })
}

// True when `draft` differs from the saved baseline it would be reconciled
// against on submit. The baseline is the route currently selected for editing
// (`getRouteDraft(document, selectedRoutePath)`) or, for a brand-new route
// (no selection), an empty draft -- so the Save button stays dead until the
// user has actually entered something or changed the saved route. Only the
// meaningful content is compared (see `routeDraftSignature`).
export function routeDraftHasPendingChanges (
    document:CanonicalApiDocument,
    selectedRoutePath:string | null,
    draft:Pick<
        RouteDraftInput,
        'pathInput' | 'selectedMethods' | 'pathParameters' | 'operations'
    >
):boolean {
    const baseline = selectedRoutePath !== null ?
        getRouteDraft(document, selectedRoutePath) :
        null
    const baselineSignature = baseline ?
        routeDraftSignature(baseline) :
        routeDraftSignature({
            pathInput: '',
            selectedMethods: [],
            pathParameters: [],
            operations: {},
        })
    return routeDraftSignature(draft) !== baselineSignature
}

export function updateRouteDraft (
    document:CanonicalApiDocument,
    draft:RouteDraftInput,
    selectedRoute:SelectedRouteIdentity
):ApplyRouteDraftResult {
    const nextDocument:CanonicalApiDocument = {
        ...document,
        paths: { ...document.paths }
    }

    const existingEntry = {
        ...(nextDocument.paths[selectedRoute.originalPath] || {})
    }

    selectedRoute.originalMethods.forEach(method => {
        delete existingEntry[method]
    })

    // A path item with no remaining HTTP operations is meaningless even when
    // it still carries shared path-level parameters. On a rename that empties
    // the old path of methods, drop it entirely rather than leaving an orphan
    // parameters-only entry behind.
    const hasRemainingOperation = SUPPORTED_METHODS.some(
        method => existingEntry[method]
    )

    if (hasRemainingOperation) {
        nextDocument.paths[selectedRoute.originalPath] = existingEntry
    } else {
        delete nextDocument.paths[selectedRoute.originalPath]
    }

    return applyRouteDraft(nextDocument, draft)
}

/**
 * Remove a route (a single `paths` key and ALL of its method operations and
 * shared parameters) from the document. The input is never mutated: a new
 * document with a copied `paths` map is returned. The path is normalized via
 * the same `normalizePath` used by `getRouteDraft` / `updateRouteDraft`, so
 * lookups match regardless of case or a missing leading slash. Removing an
 * unknown / already-absent path is a safe no-op (idempotent).
 */
export function removeRoute (
    document:CanonicalApiDocument,
    path:string
):CanonicalApiDocument {
    const normalized = normalizePath(path)
    const nextPaths = { ...document.paths }
    delete nextPaths[normalized]
    return {
        ...document,
        paths: nextPaths,
    }
}

interface OptionalExpansion {
    basePath:string;
    expandedPath:string;
    paramName:string;
}

function expandOptionalPathParam (
    normalizedPath:string
):OptionalExpansion | null {
    const segments = normalizedPath.split('/')
    const last = segments[segments.length - 1]

    const curlyMatch = last.match(/^\{([^}]+)\}\?$/)
    if (curlyMatch) {
        const name = curlyMatch[1]
        const prefix = segments.slice(0, -1)
        return {
            basePath: prefix.join('/') || '/',
            expandedPath: [...prefix, `{${name}}`].join('/'),
            paramName: name,
        }
    }

    const colonMatch = last.match(/^:([A-Za-z_][A-Za-z0-9_]*)\?$/)
    if (colonMatch) {
        const name = colonMatch[1]
        const prefix = segments.slice(0, -1)
        return {
            basePath: prefix.join('/') || '/',
            expandedPath: [...prefix, `{${name}}`].join('/'),
            paramName: name,
        }
    }

    return null
}

function applyOptionalExpansion (
    document:CanonicalApiDocument,
    expansion:OptionalExpansion,
    methods:HttpMethod[],
    operations:Partial<Record<HttpMethod, RouteOperation>>,
    normalizedPathParams:OpenApiParameter[],
    inputPath:string
):ApplyRouteDraftResult {
    const { basePath, expandedPath, paramName } = expansion

    const baseExisting = document.paths[basePath] || {}
    const dupBase = methods.find(m => baseExisting[m])
    if (dupBase) {
        return {
            ok: false,
            error: `Route ${basePath.toLowerCase()} already has ` +
                `${dupBase.toUpperCase()}.`
        }
    }

    const expandedExisting = document.paths[expandedPath] || {}
    const dupExpanded = methods.find(m => expandedExisting[m])
    if (dupExpanded) {
        return {
            ok: false,
            error: `Route ${expandedPath.toLowerCase()} already has ` +
                `${dupExpanded.toUpperCase()}.`
        }
    }

    // Base path: shared path params excluding the optional one.
    const baseParams = normalizedPathParams.filter(
        p => !(p.in === 'path' && p.name === paramName)
    )
    const nextBase:PathItem = { ...baseExisting }
    methods.forEach(m => { nextBase[m] = operations[m] })
    if (baseParams.length > 0) {
        nextBase.parameters = baseParams
    } else {
        delete nextBase.parameters
    }

    // Expanded path: include the optional param (existing or synthesized).
    const existing = normalizedPathParams.find(
        p => p.in === 'path' && p.name === paramName
    )
    const pathParam:OpenApiParameter = existing || {
        name: paramName,
        in: 'path',
        required: true,
        description: '',
        schema: { type: 'string' },
    }
    const expandedParams = [
        ...normalizedPathParams.filter(
            p => !(p.in === 'path' && p.name === paramName)
        ),
        pathParam,
    ]
    const nextExpanded:PathItem = { ...expandedExisting }
    methods.forEach(m => { nextExpanded[m] = operations[m] })
    nextExpanded.parameters = expandedParams

    return {
        ok: true,
        path: inputPath,
        methods,
        document: {
            ...document,
            paths: {
                ...document.paths,
                [basePath]: nextBase,
                [expandedPath]: nextExpanded,
            }
        }
    }
}

function normalizeParameters (parameters:ParameterEntry[]):OpenApiParameter[] {
    if (!parameters || parameters.length === 0) return []

    return parameters
        .map(param => ({
            name: param.name.trim(),
            in: param.in,
            type: param.type,
            required: param.in === 'path' ? true : param.required,
            description: param.description.trim(),
        }))
        .filter(param => {
            if (!param.name) return false
            if (param.in === 'path') return true
            return !!param.description
        })
        .map(toOpenApiParameter)
}

function validateParameters (parameters:ParameterEntry[]):string | null {
    for (const parameter of parameters) {
        if (!parameter.name.trim()) {
            return 'Parameter name and description are required.'
        }
        if (parameter.in === 'query' && !parameter.description.trim()) {
            return 'Parameter name and description are required.'
        }
    }

    return null
}

export function validateResponses (
    entries:ResponseEntry[]
):string | null {
    const seen = new Set<string>()
    for (const entry of entries) {
        const status = entry.status.trim()
        if (status === '') {
            return 'Response status code is required.'
        }
        if (!entry.description.trim()) {
            return 'Response description is required.'
        }
        if (((entry.schema || '').trim() !== '' ||
            (entry.example || '').trim() !== '') &&
            (entry.mediaType || '').trim() === '') {
            return `Response ${status} media type is required.`
        }
        if (seen.has(status)) {
            return `Duplicate response status code ${status}.`
        }
        seen.add(status)

        // Validate structured model for object-kind responses.
        if (mediaTypeKind(entry.mediaType || 'application/json') ===
            'object' && entry.schemaModel) {
            const modelError = validateSchemaModel(entry.schemaModel)
            if (modelError) return `Response ${status}: ${modelError}`
        }
    }
    return null
}

// Validate a structured schema tree for errors that gate submission:
// duplicate property names within one object node, and file fields with a
// cleared contentMediaType. Returns an error message or null (mirrors
// validateResponses). Empty-named properties are ignored (dropped at build,
// AC4.7).
export function validateSchemaModel (node:SchemaNode):string | null {
    if (node.file) {
        if ((node.contentMediaType || '').trim() === '') {
            return 'File field requires a content media type.'
        }
    }
    if (node.type === 'object') {
        const props = node.properties || []
        const seen = new Set<string>()
        for (const prop of props) {
            const name = prop.name.trim()
            if (name === '') continue
            if (seen.has(name)) {
                return `Duplicate property name "${name}".`
            }
            seen.add(name)
        }
        for (const prop of props) {
            if (prop.name.trim() === '') continue
            const childError = validateSchemaModel(prop.schema)
            if (childError) return childError
        }
    }
    if (node.type === 'array' && node.items) {
        const itemError = validateSchemaModel(node.items)
        if (itemError) return itemError
    }
    return null
}

const RE_DATE = /^\d{4}-\d{2}-\d{2}$/
const RE_DATE_TIME =
    /^\d{4}-\d{2}-\d{2}[Tt]\d{2}:\d{2}:\d{2}(\.\d+)?([Zz]|[+-]\d{2}:\d{2})$/
const RE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const RE_UUID =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function isUriLike (value:string):boolean {
    // Absolute URI with a scheme and authority, e.g. https://example.com.
    // Conservative on purpose so plain strings get no format.
    return /^[a-z][a-z0-9+.-]*:\/\//i.test(value)
}

// Detect a common string format. Order matters: date-time before date,
// uuid/email before the looser uri check.
export function detectStringFormat (
    value:string
):StringFormat | undefined {
    if (RE_DATE_TIME.test(value)) return 'date-time'
    if (RE_DATE.test(value)) return 'date'
    if (RE_UUID.test(value)) return 'uuid'
    if (RE_EMAIL.test(value)) return 'email'
    if (isUriLike(value)) return 'uri'
    return undefined
}

// Infer a JSON Schema (plain object) from a single parsed example value.
// Recurses into objects and arrays; marks all present object keys required;
// detects common string formats. Total over any JSON value, including null.
export function inferSchemaFromExample (
    value:unknown
):Record<string, unknown> {
    if (value === null) {
        // Permissive: empty schema, no incorrect type. (AC1.6)
        return {}
    }
    if (Array.isArray(value)) {
        const schema:Record<string, unknown> = { type: 'array' }
        if (value.length > 0) {
            schema.items = inferSchemaFromExample(value[0])
        }
        return schema
    }
    const kind = typeof value
    if (kind === 'object') {
        const obj = value as Record<string, unknown>
        const keys = Object.keys(obj)
        const schema:Record<string, unknown> = { type: 'object' }
        if (keys.length > 0) {
            const properties:Record<string, unknown> = {}
            for (const key of keys) {
                properties[key] = inferSchemaFromExample(obj[key])
            }
            schema.properties = properties
            schema.required = [...keys]
        }
        return schema
    }
    if (kind === 'string') {
        const schema:Record<string, unknown> = { type: 'string' }
        const format = detectStringFormat(value as string)
        if (format) schema.format = format
        return schema
    }
    if (kind === 'number') return { type: 'number' }
    if (kind === 'boolean') return { type: 'boolean' }
    return {}
}

// Emit only the non-empty fields of an xml modifier (or undefined).
function serializeXml (xml?:XmlModifier):XmlModifier | undefined {
    if (!xml) return undefined
    const out:XmlModifier = {}
    if (xml.name && xml.name.trim() !== '') out.name = xml.name.trim()
    if (xml.namespace && xml.namespace.trim() !== '') {
        out.namespace = xml.namespace.trim()
    }
    if (xml.prefix && xml.prefix.trim() !== '') {
        out.prefix = xml.prefix.trim()
    }
    if (xml.attribute) out.attribute = true
    if (xml.wrapped) out.wrapped = true
    return Object.keys(out).length > 0 ? out : undefined
}

// Convert a structured SchemaNode tree into a plain JSON Schema object,
// ready to embed in the OpenAPI document. Pure; recurses to any depth.
export function schemaNodeToJsonSchema (
    node:SchemaNode
):Record<string, unknown> {
    // A file field is a binary string regardless of node.type.
    if (node.file) {
        const out:Record<string, unknown> = {
            type: 'string',
            contentMediaType: (node.contentMediaType || '').trim() ||
                'application/octet-stream',
        }
        const fileXml = serializeXml(node.xml)
        if (fileXml) out.xml = fileXml
        return out
    }

    const out:Record<string, unknown> = { type: node.type }
    if (node.type === 'string' && node.format) {
        out.format = node.format
    }
    if (node.type === 'object') {
        // Drop empty-named properties (AC4.7: silent, not an error).
        const named = (node.properties || []).filter(
            p => p.name.trim() !== ''
        )
        if (named.length > 0) {
            const properties:Record<string, unknown> = {}
            const required:string[] = []
            for (const prop of named) {
                properties[prop.name.trim()] =
                    schemaNodeToJsonSchema(prop.schema)
                if (prop.required) required.push(prop.name.trim())
            }
            out.properties = properties
            if (required.length > 0) out.required = required
        }
    }
    if (node.type === 'array' && node.items) {
        out.items = schemaNodeToJsonSchema(node.items)
    }
    const xml = serializeXml(node.xml)
    if (xml) out.xml = xml
    return out
}

// The auto-derived JSON Schema for binary/text media types (no editable
// rows). Returns null for json/object kinds (those use other paths).
export function derivedBodySchema (
    mediaType:string
):Record<string, unknown> | null {
    const kind = mediaTypeKind(mediaType)
    if (kind === 'binary') {
        const base = (mediaType || '').split(';')[0].trim()
        return { type: 'string', contentMediaType: base }
    }
    if (kind === 'text') return { type: 'string' }
    return null
}

function toSchemaNodeType (value:unknown):SchemaNodeType {
    const allowed:SchemaNodeType[] = [
        'string', 'number', 'integer', 'boolean', 'object', 'array'
    ]
    return allowed.includes(value as SchemaNodeType) ?
        value as SchemaNodeType :
        'object'
}

function isStringFormat (value:unknown):value is StringFormat {
    const allowed:StringFormat[] = [
        'date-time', 'date', 'email', 'uri', 'uuid', 'password'
    ]
    return allowed.includes(value as StringFormat)
}

function parseXml (value:unknown):XmlModifier | undefined {
    if (!value || typeof value !== 'object') return undefined
    const v = value as Record<string, unknown>
    const out:XmlModifier = {}
    if (typeof v.name === 'string') out.name = v.name
    if (typeof v.namespace === 'string') out.namespace = v.namespace
    if (typeof v.prefix === 'string') out.prefix = v.prefix
    if (v.attribute === true) out.attribute = true
    if (v.wrapped === true) out.wrapped = true
    return Object.keys(out).length > 0 ? out : undefined
}

// Best-effort inverse of schemaNodeToJsonSchema: reconstruct the in-memory
// SchemaNode tree from a raw JSON Schema object loaded from a saved document.
export function schemaNodeToDraft (obj:unknown):SchemaNode {
    if (!obj || typeof obj !== 'object') return emptySchemaNode()
    const o = obj as Record<string, unknown>

    // Binary string field: { type:'string', contentMediaType }.
    if (o.type === 'string' && typeof o.contentMediaType === 'string') {
        const node:SchemaNode = {
            type: 'string',
            file: true,
            contentMediaType: o.contentMediaType,
        }
        const fileXml = parseXml(o.xml)
        if (fileXml) node.xml = fileXml
        return node
    }

    const type = toSchemaNodeType(o.type)
    const node:SchemaNode = { type }
    if (type === 'string' && isStringFormat(o.format)) {
        node.format = o.format
    }
    if (type === 'object') {
        const props = (o.properties && typeof o.properties === 'object') ?
            o.properties as Record<string, unknown> :
            {}
        const required = Array.isArray(o.required) ?
            (o.required as unknown[]).filter(
                (r):r is string => typeof r === 'string'
            ) :
            []
        node.properties = Object.keys(props).map(name => ({
            name,
            required: required.includes(name),
            schema: schemaNodeToDraft(props[name]),
        }))
    }
    if (type === 'array') {
        node.items = schemaNodeToDraft(o.items)
    }
    const xml = parseXml(o.xml)
    if (xml) node.xml = xml
    return node
}

export type LenientParseResult =
    | { ok:true; value:unknown }
    | { ok:false; error:string }

// Parse a lenient JSON5-style object literal (unquoted keys, single
// quotes, trailing commas, comments) with NO code execution. JSON5.parse
// never evaluates expressions, so `{ x: 1 + 1 }` is a syntax error rather
// than `2`.
export function parseRequestBody (text:string):LenientParseResult {
    try {
        return { ok: true, value: JSON5.parse(text) }
    } catch (_err) {
        return {
            ok: false,
            error: 'Request body must be a valid object literal ' +
                '(JSON or JS-style).'
        }
    }
}

type BuildBodyResult =
    | { ok:true; body?:RequestBodyObject }
    | { ok:false; error:string }

function buildRequestBody (
    mediaTypeInput:string,
    schemaText:string,
    exampleText:string,
    methods:HttpMethod[],
    schemaModel?:SchemaNode
):BuildBodyResult {
    const mediaType = (mediaTypeInput || '').trim() || 'application/json'
    const kind = mediaTypeKind(mediaType)
    const hasText = (schemaText || '').trim() !== '' ||
        (exampleText || '').trim() !== ''
    const hasModel = kind === 'object' && !!schemaModel &&
        schemaModelHasContent(schemaModel)
    // The request-body media-type picker is constrained to
    // REQUEST_BODY_MEDIA_TYPES (json + multipart), so `kind` here is always
    // 'json' or 'object'. Unlike the response path (toMediaTypeContent emits a
    // derived schema for binary/text), a binary/text request body would fall
    // through to `hasContent === false` and emit no body. If a binary/text
    // type is ever added to that picker, fold it into `hasContent` here.
    const hasContent = hasText || hasModel
    if (!hasContent) {
        return { ok: true, body: undefined }
    }

    const bodyMethods = methods.filter(
        method => ['post', 'put', 'patch', 'delete'].includes(method)
    )
    if (bodyMethods.length === 0) {
        return {
            ok: false,
            error: 'Request body may only be defined for non-GET routes.'
        }
    }

    // Validate structured model if present and kind is object.
    if (kind === 'object' && schemaModel) {
        const modelError = validateSchemaModel(schemaModel)
        if (modelError) {
            const label = bodyMethods
                .map(method => method.toUpperCase())
                .join('/')
            return {
                ok: false,
                error: `Request body for ${label}: ${modelError}`
            }
        }
    }

    const built = toMediaTypeContent(
        mediaType, schemaText || '', exampleText || '', schemaModel
    )
    if (!built.ok) {
        const label = bodyMethods
            .map(method => method.toUpperCase())
            .join('/')
        return {
            ok: false,
            error: `Request body for ${label} must be a valid object ` +
                'literal (JSON or JS-style).'
        }
    }

    return {
        ok: true,
        body: { content: { [mediaType]: built.content! } }
    }
}

type BuildResponsesResult =
    | { ok:true; responses?:Record<string, ResponseObject> }
    | { ok:false; error:string }

export function buildResponses (
    entries:ResponseEntry[]
):BuildResponsesResult {
    if (entries.length === 0) {
        return { ok: true, responses: undefined }
    }
    const responses:Record<string, ResponseObject> = {}
    for (const entry of entries) {
        const status = entry.status.trim()
        const built = toResponseObject(entry)
        if (!built.ok) {
            return {
                ok: false,
                error: `Response ${status} body must be a valid ` +
                    'object literal (JSON or JS-style).'
            }
        }
        responses[status] = built.value
    }
    return { ok: true, responses }
}

export function normalizePath (pathInput:string):string {
    const trimmed = pathInput.trim()
    if (!trimmed) return ''
    const withSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`
    return withSlash.split('/').map(segment => {
        if (/^\{[^}]+\}\??$/.test(segment)) return segment
        if (/^:[A-Za-z_][A-Za-z0-9_]*\??$/.test(segment)) return segment
        return segment.toLowerCase()
    }).join('/')
}

function normalizeMethods (selectedMethods:string[]):HttpMethod[] {
    const normalized = selectedMethods
        .map(method => method.trim().toLowerCase())
        .filter((method):method is HttpMethod => {
            return SUPPORTED_METHODS.includes(method as HttpMethod)
        })

    return Array.from(new Set(normalized))
}

function hasInvalidPathWhitespace (path:string):boolean {
    return /\s/.test(path)
}

export interface PathParameterToken {
    name:string;
    optional:boolean;
}

export function parsePathParameters (
    pathInput:string
):PathParameterToken[] {
    const results:PathParameterToken[] = []
    const segments = pathInput.split('/')

    for (const segment of segments) {
        // Curly-brace syntax: {name} or {name}?
        const curlyMatch = segment.match(/^\{([^}]+)\}(\?)?$/)
        if (curlyMatch) {
            const name = curlyMatch[1].trim()
            if (name) {
                results.push({ name, optional: curlyMatch[2] === '?' })
            }
            continue
        }

        // Colon syntax: :name or :name?
        const colonMatch = segment.match(/^:([A-Za-z_][A-Za-z0-9_]*)(\?)?$/)
        if (colonMatch) {
            results.push({
                name: colonMatch[1],
                optional: colonMatch[2] === '?'
            })
        }
    }

    return results
}
